import {
  PublicClientApplication,
  type AccountInfo,
  type AuthenticationResult,
} from '@azure/msal-browser';

// Direct Microsoft Entra ID login config. Values come from environment
// settings (not hardcoded) so the same build works across dev/staging/prod
// once each environment's redirect URI is registered on the app registration.
const msalConfig = {
  auth: {
    clientId: import.meta.env.VITE_AZURE_AD_CLIENT_ID || '',
    authority: `https://login.microsoftonline.com/${import.meta.env.VITE_AZURE_AD_TENANT_ID || ''}`,
    redirectUri:
      import.meta.env.VITE_AZURE_AD_REDIRECT_URI || `${globalThis.location.origin}/`,
  },
  cache: {
    // sessionStorage on purpose: MSAL's own token cache should not persist
    // across browser restarts. The app's long-lived session is the
    // separately-issued auth_token in localStorage, unaffected by this.
    cacheLocation: 'sessionStorage' as const,
    storeAuthStateInCookie: false,
  },
};

// Only the profile fields the backend's Graph-verification call needs.
const GRAPH_SCOPES = ['User.Read'];

// prompt: 'select_account' always shows Microsoft's account picker on the
// interactive login, even when the browser already has one account signed
// in - without it, Microsoft may silently proceed with whatever account is
// already active. Interactive-only: not valid on a silent token request.
const graphLoginRequest = {scopes: GRAPH_SCOPES, prompt: 'select_account' as const};

let msalInstance: PublicClientApplication | null = null;
let initPromise: Promise<PublicClientApplication> | null = null;

const getMsalInstance = async (): Promise<PublicClientApplication> => {
  if (msalInstance) return msalInstance;
  if (!initPromise) {
    initPromise = (async () => {
      const instance = new PublicClientApplication(msalConfig);
      await instance.initialize();
      msalInstance = instance;
      return instance;
    })();
  }
  return initPromise;
};

/**
 * Processes the browser landing back from Microsoft's login page after a
 * full-page redirect. Safe to call on every app load - resolves to `null`
 * when there is nothing to process.
 */
export const handleMsalRedirect = async (): Promise<AuthenticationResult | null> => {
  const instance = await getMsalInstance();
  return instance.handleRedirectPromise();
};

/**
 * Starts a Microsoft sign-in via a full-page redirect to Microsoft's own
 * login page.
 *
 * Deliberately NOT using MSAL's "silent" (hidden-iframe) login attempt:
 * that technique needs to read Microsoft's session cookie from inside a
 * hidden iframe, which modern browsers increasingly block by default as a
 * third-party-cookie privacy protection. When blocked, the attempt doesn't
 * fail cleanly - it can hang indefinitely with no error and no visible
 * feedback, which is worse than always showing the (brief) redirect. A
 * full-page redirect doesn't rely on cross-domain iframe cookie access at
 * all, so it can't fail this way.
 */
export const signInWithMicrosoft = async (): Promise<void> => {
  const instance = await getMsalInstance();
  await instance.loginRedirect(graphLoginRequest);
};

/**
 * Returns a fresh Microsoft Graph access token for `account` (or the first
 * cached account when omitted), or `null` if no account is available.
 */
export const getMicrosoftAccessToken = async (
  account?: AccountInfo,
): Promise<string | null> => {
  const instance = await getMsalInstance();
  const targetAccount = account || instance.getAllAccounts()[0];
  if (!targetAccount) return null;

  const result = await instance.acquireTokenSilent({
    scopes: GRAPH_SCOPES,
    account: targetAccount,
  });
  return result.accessToken;
};
